/**
 * Brand identity — which brand an AI answer's entry names, and which domain (if any) is that brand's.
 *
 * **Client-safe on purpose (ARC-01).** The answer chips and the ranking table are client components,
 * so this module imports only `tldts` (via `parse`) and the platform-host list from
 * `@/lib/barnacle/platform-domains` (a leaf module). No Supabase, no `ai`, no Sentry.
 *
 * Three rules, from the one-brand-one-number decisions (P1, P2) and measurement-correctness (P8):
 *
 * **P1 — identity is never a domain.** A brand is the tracked competitor (matched by its name or an
 * alias) when there is one, else the letters-and-digits name key. "Peec AI" and "Peec.ai" are one
 * brand, "OtterlyAI" and "Otter.ai" are two, "Senja Pro" is not "Senja". Over-merging two different
 * companies is the worst failure here, so the key strips only legal suffixes (Inc, LLC, Ltd, Co,
 * Corp, GmbH) and never a product word such as "CRM" or "App" (that is what
 * `canonicalBrandKey` does, and why it is not used). A domain is evidence for or against merging two
 * spellings (P8 below), never the key itself.
 *
 * **P8 — two keys are one brand only on evidence.** `resolveBrandSpellings` (in `spelling-resolution.ts`) takes the spellings the
 * answers used (and the hosts those answers cited) and merges "Otterly.ai"/"Otterly", "Scrunch"/
 * "Scrunch AI" or "HubSpot"/"HubSpot AI Brand Tracker" when a candidate pair has confirming evidence
 * and no blocking evidence. The result is a `BrandResolution`, consulted by `resolveBrandIdentity`
 * after the own brand, a stored competitor id and the tracked keys.
 *
 * **P2 — a domain is an attribute, shown only when it is the brand's own.** The one an editor typed
 * for a tracked competitor, the project's own domain, or a domain whose registrable name is the
 * brand's name (peec.ai for "Peec AI"). Anything else an LLM guessed (otter.ai for "Otterly AI") is
 * dropped, and the caller draws the initial instead of a logo.
 *
 * ## `brandNameKey` — the algorithm (the SQL `public.brand_name_key(text)` mirrors it exactly)
 *
 *   1. Unicode-normalise (NFKC) and lower-case
 *   2. every character that is not a letter, mark, digit, whitespace or `-` becomes a space
 *      (punctuation → space)
 *   3. runs of whitespace and hyphens collapse to one `-`
 *   4. trim leading and trailing `-`
 *   5. strip one trailing legal suffix: `-(inc|llc|ltd|co|corp|gmbh)$`, then trailing `-`
 *   6. drop everything that is not a letter, mark or digit
 *
 * The key is Unicode-aware so a name with no Latin letters ("ヤフー", "Яндекс") still has one — an
 * ASCII-only key was `''` for it, which made every such brand "no identity" (PRD-01). For a pure
 * ASCII name the result is exactly the letters-and-digits key. The SQL twin uses `[[:alnum:]]` under
 * the database's UTF-8 ctype for the same classes.
 */
import { parse } from 'tldts'
import {
  MULTI_TENANT_EXTRA_DOMAINS,
  PLATFORM_SOURCE_DOMAINS,
} from '@/lib/barnacle/platform-domains'

// ─── Name key ────────────────────────────────────────────────────────────────

const NOT_NAME_CHAR = /[^\p{L}\p{M}\p{N}\s-]/gu
const LEGAL_SUFFIX = /-(inc|llc|ltd|co|corp|gmbh)$/
const NOT_KEY_CHAR = /[^\p{L}\p{M}\p{N}]/gu

/** The letters-and-digits key two spellings of one brand share ("Peec AI" and "Peec.ai" → `peecai`). `''` for a blank name. */
export function brandNameKey(name: string | null | undefined): string {
  if (!name) return ''
  const slug = name
    .normalize('NFKC')
    .toLowerCase()
    .replace(NOT_NAME_CHAR, ' ')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(LEGAL_SUFFIX, '')
  return slug.replace(NOT_KEY_CHAR, '')
}

// ─── Index ───────────────────────────────────────────────────────────────────

/** The project's own brand, as the answers' identity resolver needs it. */
export interface IdentityOwnBrand {
  /** Nullable so a project read that lacks the field degrades to "no own-brand key", never a throw. */
  name: string | null | undefined
  aliases?: readonly string[] | null
  domain?: string | null
}

/** One tracked competitor row. `created_at` orders a cluster's winner; absent, list order decides. */
export interface IdentityTrackedBrand {
  id: string
  name: string
  aliases?: readonly string[] | null
  domain?: string | null
  created_at?: string | null
}

/** The own brand's identity id (not a name key: those hold only `[a-z0-9]`, so it cannot collide). */
export const OWN_BRAND_IDENTITY_ID = '#own'

/**
 * A group of spellings the evidence merged: every key in it resolves to `id` and `label`. `tracked`
 * is a name key of the tracked competitor the group joined (resolved through the tracked clusters,
 * so a stale entry whose competitor is gone degrades to the unmerged key), else `null`.
 */
export interface ResolvedGroup {
  id: string
  label: string
  tracked: string | null
}

/**
 * The evidence resolution (P8) in a form that crosses the cache and the network: only the name keys
 * that merge into a group, plus — for the tracked competitors that took spellings in — the merged
 * keys by competitor id, so a SQL read can ask for them by name key.
 */
export interface BrandResolution {
  groups: Record<string, ResolvedGroup>
  mergedKeysByCompetitorId: Record<string, string[]>
}

/** Nothing merges: each spelling is its own row, today's grouping. Also the degraded-read value. */
export const EMPTY_BRAND_RESOLUTION: BrandResolution = { groups: {}, mergedKeysByCompetitorId: {} }

/**
 * The `p_merged_names` argument of the window RPCs, present ONLY when a competitor took a spelling in.
 * Sending `{}` would name the new 5-argument signature for the projects with nothing merged, and in the
 * window where the code is deployed before its migration applies (DAT-28) every one of them would get
 * PGRST202; omitted, the pre-migration function serves them with the same counts.
 */
export function mergedNamesArg(
  mergedKeysByCompetitorId: Record<string, string[]> | undefined
): { p_merged_names: Record<string, string[]> } | Record<string, never> {
  return mergedKeysByCompetitorId && Object.keys(mergedKeysByCompetitorId).length > 0
    ? { p_merged_names: mergedKeysByCompetitorId }
    : {}
}

interface Cluster {
  id: string
  label: string
  domain: string | null
}

export interface BrandIdentityIndex {
  ownKeys: ReadonlySet<string>
  ownLabel: string
  ownDomain: string | null
  byCompetitorId: ReadonlyMap<string, Cluster>
  byKey: ReadonlyMap<string, Cluster>
  /** The evidence resolution's groups by name key (`BrandResolution.groups`). */
  resolved: ReadonlyMap<string, ResolvedGroup>
}

function compareTracked(
  a: { row: IdentityTrackedBrand; index: number },
  b: { row: IdentityTrackedBrand; index: number }
): number {
  const am = a.row.created_at ? Date.parse(a.row.created_at) : Number.NaN
  const bm = b.row.created_at ? Date.parse(b.row.created_at) : Number.NaN
  if (!Number.isNaN(am) && !Number.isNaN(bm)) {
    if (am !== bm) return am - bm
    return a.row.id < b.row.id ? -1 : a.row.id > b.row.id ? 1 : a.index - b.index
  }
  // A missing `created_at` (the claim page's featured rows): list order is the tiebreak.
  return a.index - b.index
}

function rowKeys(row: IdentityTrackedBrand): string[] {
  return [row.name, ...(row.aliases ?? [])].map(brandNameKey).filter((key) => key !== '')
}

/**
 * The resolver's lookup tables: tracked competitors clustered when any name or alias key is shared
 * (a fixpoint, so alias chains close), the cluster's identity being its oldest row — the winner the
 * database merge picks, so read time and the database agree. Two rows whose user-entered domains
 * differ never cluster, whatever their names (AC-9): an alias they share does not make them one
 * brand. `resolution` adds the evidence groups (P8); absent, nothing but the name key merges.
 */
export function buildBrandIdentityIndex(
  tracked: readonly IdentityTrackedBrand[],
  own: IdentityOwnBrand,
  resolution: BrandResolution = EMPTY_BRAND_RESOLUTION
): BrandIdentityIndex {
  const parent = tracked.map((_, i) => i)
  const find = (i: number): number => {
    let root = i
    while (parent[root] !== root) root = parent[root]!
    while (parent[i] !== root) {
      const next = parent[i]!
      parent[i] = root
      i = next
    }
    return root
  }

  // The entered domains of each cluster, by root.
  const domainsOf = tracked.map((row) => {
    const host = normalizeHost(row.domain)
    return new Set<string>(host ? [host] : [])
  })
  const firstWithKey = new Map<string, number>()
  tracked.forEach((row, i) => {
    for (const key of rowKeys(row)) {
      const seen = firstWithKey.get(key)
      if (seen === undefined) {
        firstWithKey.set(key, i)
        continue
      }
      const rootA = find(i)
      const rootB = find(seen)
      if (rootA === rootB) continue
      const a = domainsOf[rootA]!
      const b = domainsOf[rootB]!
      // Blocking evidence: both clusters carry an entered domain and they share none.
      if (a.size > 0 && b.size > 0 && ![...a].some((host) => b.has(host))) continue
      parent[rootA] = rootB
      for (const host of a) b.add(host)
    }
  })

  const members = new Map<number, Array<{ row: IdentityTrackedBrand; index: number }>>()
  tracked.forEach((row, index) => {
    const root = find(index)
    const list = members.get(root) ?? []
    list.push({ row, index })
    members.set(root, list)
  })

  const byCompetitorId = new Map<string, Cluster>()
  const byKey = new Map<string, Cluster>()
  for (const list of members.values()) {
    const ordered = [...list].sort(compareTracked)
    const winner = ordered[0]!.row
    const domain =
      ordered.map(({ row }) => normalizeHost(row.domain)).find((host) => host !== null) ?? null
    const cluster: Cluster = {
      id: brandNameKey(winner.name) || `tracked:${winner.id}`,
      label: winner.name.trim(),
      domain,
    }
    for (const { row } of ordered) {
      byCompetitorId.set(row.id, cluster)
      for (const key of rowKeys(row)) byKey.set(key, cluster)
    }
  }

  const ownName = own.name ?? ''
  return {
    ownKeys: new Set(rowKeys({ id: '', name: ownName, aliases: own.aliases })),
    ownLabel: ownName.trim(),
    ownDomain: normalizeHost(own.domain),
    byCompetitorId,
    byKey,
    resolved: new Map(Object.entries(resolution.groups)),
  }
}

// ─── Resolve ─────────────────────────────────────────────────────────────────

/** The fields of one `detected_brands` entry that identity reads. */
interface IdentityEntry {
  name: string
  type?: string
  competitor_id?: string | null
}

export interface BrandIdentity {
  /** Groups spellings: `OWN_BRAND_IDENTITY_ID`, the tracked cluster's name key, or the untracked name key. */
  id: string
  /** The tracked (or own) name; for an untracked brand, this entry's spelling (see `mostFrequentSpelling`). */
  label: string
  kind: 'own' | 'tracked' | 'untracked'
  /** The domain an editor entered (tracked cluster) or the project's own; `null` otherwise. */
  trackedDomain: string | null
  /**
   * Set when the evidence resolution grouped this spelling (P8): `label` is then the group's name
   * (AC-10: the tracked name, the product line's parent, else the spelling named most), not a
   * spelling to be re-voted by the caller.
   */
  merged?: true
}

/**
 * Which brand an answer's entry names: the own brand (`type: 'brand'`, or a key equal to the brand's
 * name or alias key) → a tracked cluster (by `competitor_id`, or a name key equal to a name or alias
 * key in it) → a group the evidence resolution merged this key into → else its own name key. `null`
 * for an entry with no key, which counts nowhere.
 */
export function resolveBrandIdentity(
  index: BrandIdentityIndex,
  entry: IdentityEntry
): BrandIdentity | null {
  const key = brandNameKey(entry.name)

  // The own brand and a stored `competitor_id` resolve without a name key: an entry whose name keys
  // to '' still belongs to the brand it was stored against (PRD-01).
  if (entry.type === 'brand' || (key !== '' && index.ownKeys.has(key))) {
    return {
      id: OWN_BRAND_IDENTITY_ID,
      label: index.ownLabel || entry.name.trim(),
      kind: 'own',
      trackedDomain: index.ownDomain,
    }
  }

  const byId = entry.competitor_id ? index.byCompetitorId.get(entry.competitor_id) : undefined
  const cluster = byId ?? (key === '' ? undefined : index.byKey.get(key))
  if (cluster) {
    return { id: cluster.id, label: cluster.label, kind: 'tracked', trackedDomain: cluster.domain }
  }
  if (key === '') return null

  const group = index.resolved.get(key)
  if (group) {
    if (group.tracked === null) {
      return {
        id: group.id,
        label: group.label,
        kind: 'untracked',
        trackedDomain: null,
        merged: true,
      }
    }
    const joined = index.byKey.get(group.tracked)
    if (joined) {
      return {
        id: joined.id,
        label: joined.label,
        kind: 'tracked',
        trackedDomain: joined.domain,
        merged: true,
      }
    }
    // The competitor this group joined is gone (a stale cached resolution): today's grouping.
  }
  return { id: key, label: entry.name.trim(), kind: 'untracked', trackedDomain: null }
}

/** The spelling used most often; ties go to the shorter spelling, then the first seen; `''` for none (AC-10). */
export function mostFrequentSpelling(spellings: readonly string[]): string {
  const counts = new Map<string, number>()
  for (const spelling of spellings) counts.set(spelling, (counts.get(spelling) ?? 0) + 1)
  let best = ''
  let bestCount = 0
  for (const [spelling, count] of counts) {
    if (count > bestCount || (count === bestCount && spelling.length < best.length)) {
      best = spelling
      bestCount = count
    }
  }
  return best
}

// ─── Verified domain ─────────────────────────────────────────────────────────

const MULTI_TENANT_DOMAINS: readonly string[] = [
  ...PLATFORM_SOURCE_DOMAINS,
  ...MULTI_TENANT_EXTRA_DOMAINS,
]

function isMultiTenantHost(host: string): boolean {
  return MULTI_TENANT_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`))
}

/** The host of a domain or URL, `www.` removed, or `null` when it holds no registrable host. */
export function normalizeHost(raw: string | null | undefined): string | null {
  const text = raw?.trim()
  if (!text) return null
  const parsed = parse(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`)
  if (!parsed.hostname || !parsed.domain) return null
  return parsed.hostname.toLowerCase().replace(/^www\./, '')
}

function collapse(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Whether the host's registrable label, alone or joined with its public suffix, is one of the name keys. */
export function hostNamesBrand(host: string, nameKeys: ReadonlySet<string>): boolean {
  const parsed = parse(`https://${host}`)
  const label = collapse(parsed.domainWithoutSuffix ?? '')
  if (label === '') return false
  return nameKeys.has(label) || nameKeys.has(label + collapse(parsed.publicSuffix ?? ''))
}

/**
 * The domain to show for a brand, or `null` — then the caller draws the initial and no domain text.
 * In order: the tracked competitor's (or the project's own) domain, which an editor entered; then
 * the first candidate — the LLM's domain, then the hosts of URLs cited in the same answer — whose
 * registrable name is the brand's name or one of its aliases (peec.ai for "Peec AI"; otter.ai is
 * not "Otterly AI", sparktoro.com is not "Goodie"). A multi-tenant host never qualifies as a
 * candidate: where a brand is published is not who it is.
 */
export function verifiedBrandDomain(input: {
  name: string
  aliases?: readonly string[] | null
  /** The domain an editor entered for this brand, or the project's own domain. */
  trackedDomain?: string | null
  candidates?: ReadonlyArray<string | null | undefined>
}): string | null {
  const tracked = normalizeHost(input.trackedDomain)
  if (tracked) return tracked

  const nameKeys = new Set(
    [input.name, ...(input.aliases ?? [])].map(brandNameKey).filter((key) => key !== '')
  )
  if (nameKeys.size === 0) return null

  for (const candidate of input.candidates ?? []) {
    const host = normalizeHost(candidate)
    if (host === null || isMultiTenantHost(host)) continue
    if (hostNamesBrand(host, nameKeys)) return host
  }
  return null
}

// ─── Applying a resolution to stored answers ─────────────────────────────────

/** The fields of a stored answer `applyBrandResolution` reads and may stamp. */
interface ResolvableAnswer {
  competitor_mentions?: ReadonlyArray<{
    competitor_id: string | null
    name: string
    mentioned: boolean
  }> | null
  detected_brands?: ReadonlyArray<{ name: string; competitor_id?: string | null }> | null
}

/**
 * The in-memory twin of the window RPCs' `p_merged_names` match (measurement-correctness B6, AC-8).
 * For each tracked competitor with merged keys, an answer whose `detected_brands` holds a name whose
 * `brandNameKey` is one of them (the key is computed from the raw name, as the SQL does with
 * `brand_name_key`) counts as naming that competitor:
 *
 * - its `competitor_mentions` entry (matched by `competitor_id`) becomes `mentioned: true`, and
 * - the matching detected entry gets the competitor's id stamped when it has none, so
 *   `matchesCompetitor` and `computeOrdinalRank` find the merged spelling like the tracked one.
 *
 * The result is a copy; a stored row is never written, and an answer the resolution does not touch
 * is returned as the same object. An empty resolution returns the input array itself, so a degraded
 * read is today's counts. The entry stays one entry per competitor: an answer naming both spellings
 * counts once.
 */
export function applyBrandResolution<T extends ResolvableAnswer>(
  results: readonly T[],
  resolution: BrandResolution
): T[] {
  const idsByKey = new Map<string, string[]>()
  for (const [competitorId, keys] of Object.entries(resolution.mergedKeysByCompetitorId)) {
    for (const key of keys) {
      if (key === '') continue
      const ids = idsByKey.get(key)
      if (ids) ids.push(competitorId)
      else idsByKey.set(key, [competitorId])
    }
  }
  if (idsByKey.size === 0) return results as T[]

  return results.map((result) => {
    const detected = result.detected_brands
    if (!detected || detected.length === 0) return result

    const named = new Set<string>()
    const stamped = [...detected]
    for (const [index, entry] of detected.entries()) {
      const ids = idsByKey.get(brandNameKey(entry.name))
      if (!ids) continue
      for (const id of ids) named.add(id)
      if (entry.competitor_id == null && ids.length === 1) {
        stamped[index] = { ...entry, competitor_id: ids[0]! }
      }
    }
    if (named.size === 0) return result

    const mentions = result.competitor_mentions
    const marked = mentions
      ? mentions.map((mention) =>
          mention.competitor_id !== null && named.has(mention.competitor_id) && !mention.mentioned
            ? { ...mention, mentioned: true }
            : mention
        )
      : mentions
    return { ...result, detected_brands: stamped, competitor_mentions: marked } as T
  })
}
